import { getMetaPreparedFromException } from './ExceptionUtil';
import { IOperationResult } from './InterfaceAndTypeUtil';
import { logError, logWarningAlt, logDebugAlt } from './LoggingUtil';
import {
  entityOnLoad,
  IEntityLookupControlRef,
  IEntityOnLoadParams,
} from './VanillaJsUtil';

type ITopNavBarWindowEntity = {
  _callFunc?: (name: string, args: unknown[]) => unknown,
  _isReady?: ITopNavBarIsReadyFunc,
  [key: string]: any,
};

type ITopNavBarGlobalWindow = Window & {
  TopNavBar?: ITopNavBarWindowEntity,
  TopNavBarConfig?: Record<string, any>,
  TopNavBarIframeWrapper?: {
    iframeCall: (funcName: string, funcArgs: any) => void,
  },
};

const getTopNavBarWindow = (): ITopNavBarGlobalWindow => window as ITopNavBarGlobalWindow;

/** @deprecated */
export const callIframedTopNavBarFunction = ({
  funcName,
  funcArgs,
}: {
  funcName: string,
  funcArgs: any,
}) => {
  try {
    if (typeof getTopNavBarWindow().TopNavBarIframeWrapper !== 'undefined') {
      getTopNavBarWindow().TopNavBarIframeWrapper!.iframeCall(funcName, funcArgs);
    } else {
      logError('TopNavBarUtil -> callIframedTopNavBarFunction', 'window.TopNavBarIframeWrapper is not defined');
    }
  } catch (exc) {
    logError('TopNavBarUtil -> callIframedTopNavBarFunction -> exc', exc);
  }
};

export type ICallTopNavBarFunctionRes<T = any> = IOperationResult<{
  funcName: string,
  funcArgs: any,
  funcRes: T,
}>;

/** @deprecated Use callTopNavBar instead — it awaits window.TopNavBar and _callFunc before calling. */
export const callTopNavBarFunction = <T = any>({
  funcName,
  funcArgs,
  debug = false,
}: {
  funcName: string,
  funcArgs: any,
  debug?: boolean,
}): ICallTopNavBarFunctionRes<T> => {
  const output: ICallTopNavBarFunctionRes<T> = {
    error: {
      code: '1',
      description: 'Failed performing "callTopNavBarFunction"'
    },
    data: {
      funcName,
      funcArgs,
      funcRes: undefined as unknown as T,
    },
  };

  try {
    const normalizedArgs =
      funcArgs === undefined || funcArgs === null
        ? []
        : Array.isArray(funcArgs)
          ? funcArgs
          : [funcArgs];

    const topNavBarWindow = getTopNavBarWindow();

    if (typeof topNavBarWindow.TopNavBar !== 'undefined') {
      if (typeof topNavBarWindow.TopNavBar._callFunc === 'function') {
        output.data.funcRes = topNavBarWindow.TopNavBar._callFunc(funcName, normalizedArgs) as T;
        output.error = { code: '0' };
      } else {
        output.error = {
          code: 'TNBCFIND',
          description: 'window.TopNavBar._callFunc is not defined',
        };
      }
    } else {
      output.error = {
        code: 'TNBNDEF',
        description: 'window.TopNavBar is not defined',
      };
    }
  } catch (exc) {
    output.error = getMetaPreparedFromException(exc);
  }

  if (debug) {
    if (output.error.code === '0') {
      logDebugAlt('TopNavBarUtil -> callTopNavBarFunction', output);
    } else {
      logWarningAlt('TopNavBarUtil -> callTopNavBarFunction', output);
    }
  }

  return output;
};

export type ITopNavBarCallFunc = <T = any>(name: string, args: unknown[]) => T;

export type ITopNavBarIsReadyFunc = () => boolean;

export type ITopNavBarOnLoadParams = Partial<IEntityOnLoadParams<ITopNavBarCallFunc>>;

export type ITopNavBarReadyOnLoadParams = Partial<IEntityOnLoadParams<ITopNavBarIsReadyFunc>>;

export const topNavBarLookupControlRef: IEntityLookupControlRef = {
  lazyHalt: false,
};

export const haltTopNavBarLookup = (
  lookupControlRef: IEntityLookupControlRef = topNavBarLookupControlRef,
) => {
  if (lookupControlRef._halt !== undefined) { // immediate halt
    lookupControlRef._halt();
  } else { // halt on next interval tick
    lookupControlRef.lazyHalt = true;
  }
};

export const callTopNavBar = async <T = any>({
  funcName,
  funcArgs,
  debug = false,
  lookup,
}: {
  funcName: string,
  funcArgs: any,
  debug?: boolean,
  lookup?: ITopNavBarOnLoadParams,
}): Promise<ICallTopNavBarFunctionRes<T>> => {
  const output: ICallTopNavBarFunctionRes<T> = {
    error: {
      code: '1',
      description: 'Failed performing "callTopNavBar"'
    },
    data: {
      funcName,
      funcArgs,
      funcRes: undefined as unknown as T,
    },
  };

  try {
    const _topNavBarEntityOnLoadDefaults: IEntityOnLoadParams<ITopNavBarCallFunc> = {
      scope: window,
      entityName: 'TopNavBar',
      subEntityName: '_callFunc',
      lookupControlRef: topNavBarLookupControlRef,
      lookupTimeout: 10000,
      lookupIntervalStep: 5,
    };

    const _isTopNavBarCallFuncReady = (
      callFunc: unknown
    ): callFunc is ITopNavBarCallFunc => typeof callFunc === 'function';

    const normalizedArgs =
      funcArgs === undefined || funcArgs === null
        ? []
        : Array.isArray(funcArgs)
          ? funcArgs
          : [funcArgs];

    let callFunc = getTopNavBarWindow().TopNavBar?._callFunc;

    if (!_isTopNavBarCallFuncReady(callFunc)) {
      const entityOnLoadParams: IEntityOnLoadParams<ITopNavBarCallFunc> = {
        ..._topNavBarEntityOnLoadDefaults,
        ...lookup,
      };

      const lookupRes = await entityOnLoad<ITopNavBarCallFunc>(entityOnLoadParams);

      if (lookupRes.error.code === '0' && _isTopNavBarCallFuncReady(lookupRes.data)) {
        callFunc = lookupRes.data;
      }
    }

    if (_isTopNavBarCallFuncReady(callFunc)) {
      output.data.funcRes = callFunc<T>(funcName, normalizedArgs);
      output.error = { code: '0' };
    } else if (typeof getTopNavBarWindow().TopNavBar !== 'undefined') {
      output.error = {
        code: 'TNBCFIND',
        description: 'window.TopNavBar._callFunc is not defined',
      };

      logError('TopNavBarUtil -> callTopNavBar', output.error.description);
    } else {
      output.error = {
        code: 'TNBNDEF',
        description: 'window.TopNavBar is not defined',
      };

      logError('TopNavBarUtil -> callTopNavBar', output.error.description);
    }
  } catch (exc) {
    output.error = getMetaPreparedFromException(exc);
  }

  if (debug) {
    if (output.error.code === '0') {
      logDebugAlt('TopNavBarUtil -> callTopNavBar', output);
    } else {
      logWarningAlt('TopNavBarUtil -> callTopNavBar', output);
    }
  }

  return output;
};

export const configTopNavBar = (prs: {
  [key: string]: any,
}) => {
  const topNavBarWindow = getTopNavBarWindow();

  // ensure TopNavBarConfig is defined
  if (typeof topNavBarWindow.TopNavBarConfig === 'undefined') {
    topNavBarWindow.TopNavBarConfig = {};
  }

  for (const [propName, propValue] of Object.entries(prs)) {
    topNavBarWindow.TopNavBarConfig![propName] = propValue;

    if (typeof topNavBarWindow.TopNavBar !== 'undefined') {
      topNavBarWindow.TopNavBar[propName] = propValue;
    }
  }
};

export type ITopNavBarReadyRes = IOperationResult<ITopNavBarCallFunc>;

export const isTopNavBarReady = ({
  onReadyCallback,
  lookup,
}: {
  onReadyCallback: (res: ITopNavBarReadyRes) => void,
  lookup?: ITopNavBarReadyOnLoadParams,
}) => {
  const _topNavBarEntityOnLoadDefaults: IEntityOnLoadParams<ITopNavBarIsReadyFunc> = {
    scope: window,
    entityName: 'TopNavBar',
    subEntityName: '_isReady',
    lookupControlRef: topNavBarLookupControlRef,
    lookupTimeout: 10000,
    lookupIntervalStep: 5,
  };

  const _isTopNavBarReady = (isReady: unknown): isReady is ITopNavBarIsReadyFunc => (
    typeof isReady === 'function' && isReady() === true
  );

  const _getTopNavBarCallFunc = (): ITopNavBarCallFunc | undefined => {
    const callFunc = getTopNavBarWindow().TopNavBar?._callFunc;

    return typeof callFunc === 'function' ? callFunc as ITopNavBarCallFunc : undefined;
  };

  const _fireReadySuccess = () => {
    const callFunc = _getTopNavBarCallFunc();

    if (callFunc) {
      onReadyCallback({
        error: { code: '0' },
        data: callFunc,
      });

      return;
    }

    onReadyCallback({
      error: {
        code: 'TNBCFIND',
        description: 'window.TopNavBar._callFunc is not defined',
      },
      data: undefined as unknown as ITopNavBarCallFunc,
    });
  };

  const _fireReadyCallback = (isReady: unknown) => {
    if (_isTopNavBarReady(isReady)) {
      _fireReadySuccess();

      return;
    }

    if (typeof getTopNavBarWindow().TopNavBar !== 'undefined') {
      onReadyCallback({
        error: {
          code: 'TNBISREADYNDEF',
          description: 'window.TopNavBar._isReady is not defined',
        },
        data: undefined as unknown as ITopNavBarCallFunc,
      });

      return;
    }

    onReadyCallback({
      error: {
        code: 'TNBNDEF',
        description: 'window.TopNavBar is not defined',
      },
      data: undefined as unknown as ITopNavBarCallFunc,
    });
  };

  const isReady = getTopNavBarWindow().TopNavBar?._isReady;

  if (_isTopNavBarReady(isReady)) {
    return _fireReadySuccess();
  }

  const entityOnLoadParams: IEntityOnLoadParams<ITopNavBarIsReadyFunc> = {
    ..._topNavBarEntityOnLoadDefaults,
    ...lookup,
  };

  void entityOnLoad<ITopNavBarIsReadyFunc>(entityOnLoadParams).then((lookupRes) => {
    if (lookupRes.error.code === '0' && _isTopNavBarReady(lookupRes.data)) {
      _fireReadySuccess();

      return;
    }

    _fireReadyCallback(lookupRes.data);
  });
};
